import { type QuizGenerateItem } from '@/api/ai/type';
import { type GenerationModeUiCohort } from '@/api/ai/useGenerationModeUiCohort';
import { type AttachmentState } from '@/shared/features/ai-quiz-generation/types';

export { type GenerationModeUiCohort };

/**
 * 사용자가 선택할 수 있는 AI 문제 생성 모드.
 * - fast: 빠르게 생성
 * - thorough: 꼼꼼하게 생성 (default)
 */
export type GenerationMode = 'fast' | 'thorough';

export const DEFAULT_GENERATION_MODE: GenerationMode = 'thorough';

/**
 * spec §4.2 selected_mode 도메인. as_is cohort 는 'default' (mode 미노출).
 */
export type SelectedMode = GenerationMode | 'default';

/**
 * Feature configuration for BuildAiModal behavior.
 *
 * Attachment upselling is handled automatically via plan, not via props.
 */
export interface FeatureConfig {
  /** Pre-fill prompt text (used for auto-trigger from landing) */
  initialPrompt?: string;

  /** Preset topic ID (used for preset-based quiz generation) */
  topicId?: string;

  /** Achievement standards feature toggle */
  achievements: { enabled: boolean };

  /** Auto-start generation on mount */
  autoTrigger: boolean;
}

/**
 * Default feature configuration for manual flow
 */
export const DEFAULT_FEATURES: FeatureConfig = {
  achievements: { enabled: true },
  autoTrigger: false,
};

/**
 * AI 생성 결과 저장 콜백.
 * generationId는 quiz item의 origin metadata로 stamp되어 다운스트림 분석(create_complete 등)에서 사용된다.
 *
 * 반환값:
 * - true: 실제 저장 성공 → 모달이 hasSaved 플래그를 올리고 `biz_ai_generation_saved` 이벤트 발화
 * - false: 호출자가 가드(예: 최대 문항 수 초과)로 저장을 거부함 → 분석 이벤트 발화 안 됨
 *
 * `void` 반환은 backward-compat 차원에서 truthy로 취급된다.
 */
export type BuildAiModalOnSave = (
  quizzes: QuizGenerateItem[],
  generationId: string,
) => boolean | void | Promise<boolean | void>;

/**
 * Props for BuildAiModal component
 */
export interface BuildAiModalProps {
  // Core modal behavior
  onClose: () => void;
  onSave: BuildAiModalOnSave;
  quizCountLimit: number;
  type?: AttachmentState['type'];

  // Feature configuration (defaults to manual flow)
  features?: FeatureConfig;

  // Analytics tracking (landing vs manual)
  triggerSource?: 'landing' | 'manual';

  // Session ID pre-generated by open() for funnel attribution
  initialGenerationId?: string;

  // UI assignment for exposing the generation mode dropdown.
  uiCohort?: GenerationModeUiCohort;
}

/**
 * Props for buildAiModal.open() API
 */
export interface BuildAiModalOpenParams {
  onSave: BuildAiModalOnSave;
  quizCountLimit: number;
  type: AttachmentState['type'] | 'floating';
  features?: FeatureConfig;
  triggerSource?: 'landing' | 'manual';
  uiCohort?: GenerationModeUiCohort;
}
